import { memo, useMemo } from 'react'
import { ConnectWalletButton } from '../../components/Buttons/ConnectWallet'
import {
  isDepositBlockedChain,
  useDepositAmount,
  useDepositing,
  useDepositToken,
  useIsDepositPage,
} from '../../store/deposit/hook'
import { StyledDepositButton } from './styles'
import { updateModal } from '../../store/app/actions/modalActions'
import { useTranslation } from 'react-i18next'
import { bn, toFixed } from '../../utils/number'
import { parseUnits } from 'ethers/lib/utils'
import { useDispatch } from 'react-redux'
import { useCurrentNetwork } from '../../store/app/hooks'
import { useDeposit } from '../../hooks/web3/useDeposit'
import { useEvmTokenBalance } from '../../store/web3/hooks'
import { useEthereumTokenInfo } from '../../hooks/web3/useEthereumTokenInfo'
import toastify from '../../components/Toastify'
import { BigNumber } from 'ethers'
import { updateDepositAmountAction } from '../../store/deposit/actions'
import { useLinkConnected } from '../../store/link/hooks'

export const DepositButton = memo(() => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const depositing = useDepositing()
  const depositAmount = useDepositAmount()
  const deposit = useDeposit()
  const isLinkConnected = useLinkConnected()
  const currentNetwork = useCurrentNetwork()
  const depositToken = useDepositToken()
  const l1Balance = useEvmTokenBalance(
    currentNetwork?.layerOneChainId,
    depositToken?.l2CurrencyId!,
  )
  const { decimals = 0, address: tokenAddress = '' } =
    useEthereumTokenInfo(
      currentNetwork?.layerOneChainId,
      depositToken?.l2CurrencyId!,
    ) ?? {}

  const isDepositPage = useIsDepositPage()

  const isEnoughBalance = useMemo(() => {
    if (!depositAmount) {
      return false
    }
    if (!decimals) {
      return false
    }
    if (!l1Balance) {
      return false
    }
    return bn(
      !depositAmount ? '0' : parseUnits(toFixed(depositAmount, 18), decimals),
    ).lte(l1Balance)
  }, [depositAmount, decimals, l1Balance])

  const sendDeposit = () => {
    if (!depositAmount) {
      toastify.warn(t('common-enter-amount'))
      return
    }
    if (!depositToken || !depositToken?.name) {
      toastify.warn(t('common-select-token'))
      return
    }

    const _amount: BigNumber = parseUnits(depositAmount, decimals)

    deposit({
      tokenSymbol: depositToken?.name,
      tokenId: depositToken?.id!,
      tokenAddress: tokenAddress!,
      amount: _amount,
      decimals,
    }).finally(() => {
      dispatch(updateDepositAmountAction(''))
    })
  }

  if (!isLinkConnected) {
    return <ConnectWalletButton />
  }

  // TODO: v3 whiteList
  // if (!inWhiteList) {
  //     return (
  //         <ActionButton variant="contained" disabled={true} fullWidth={true}>
  //             Address is not in the whitelist
  //         </ActionButton>
  //     )
  // }

  if (isDepositBlockedChain(currentNetwork?.layerOneChainId)) {
    return (
      <StyledDepositButton variant="contained" disabled fullWidth>
        {currentNetwork?.name} chain deposits temporarily disabled
      </StyledDepositButton>
    )
  }

  if (!currentNetwork?.layerOneChainId || !depositToken?.name) {
    return (
      <StyledDepositButton variant="contained" disabled fullWidth>
        {t('deposit-select-token-network')}
      </StyledDepositButton>
    )
  }

  if (!Number(depositAmount)) {
    return (
      <StyledDepositButton variant="contained" disabled fullWidth>
        {t('deposit-enter-amount')}
      </StyledDepositButton>
    )
  }
  if (!isEnoughBalance) {
    return (
      <StyledDepositButton variant="contained" disabled fullWidth>
        {t('swap-btn-insufficient')}
      </StyledDepositButton>
    )
  }
  if (depositing) {
    return (
      <StyledDepositButton variant="contained" loading disabled fullWidth>
        Depositing
      </StyledDepositButton>
    )
  }

  return (
    <StyledDepositButton
      variant="contained"
      fullWidth
      onClick={() => {
        if (isDepositPage) {
          dispatch(
            updateModal({
              modal: 'confirmOrder',
              open: true,
            }),
          )
        } else {
          sendDeposit()
        }
      }}
    >
      {t('deposit-title')}
    </StyledDepositButton>
  )
})
